/**
 * EN-1517: the per-card Cancel button in the Download Sessions panel.
 *
 * The request and response belong to EN-1515 (POST /download-commands/cancel,
 * DeviceService.cancelDownloadCommands, types in models/DownloadSession.ts).
 * EN-1515's final rule cancels cellular RFD passage/raw downloads and Iridium
 * passage downloads (ID/IDWTR) alike, whatever link the device is on now; the
 * server's `cancellable` flag on each listed row follows the same rule.
 */
import { CancelDownloadCommandResult } from '../models/DownloadSession';
import { DownloadSessionCard } from '../models/DownloadSessionCard';
import { isPlaceholderKey, isQueuedKey } from './DownloadSessionSync';

/** The fields of one EN-1515 cancel result a card reads. */
export type CancelResultInput = Pick<
  CancelDownloadCommandResult,
  'commandId' | 'downloadSessionId' | 'status' | 'message'
>;

export const CANCEL_FAILED_MESSAGE = 'Cancel failed, try again.';
export const CANCEL_NOT_QUEUED_YET_MESSAGE =
  'This download is not queued on the server yet. Try again in a moment.';
/** A 403 from the cancel endpoint, or a non-admin click. */
export const CANCEL_FORBIDDEN_MESSAGE = 'Only admins can cancel downloads.';
export const NOT_CANCELLABLE_MESSAGE =
  "Can't cancel: the download already finished or was sent.";
export const NOT_DOWNLOAD_MESSAGE = "Can't cancel: this is not a download.";

/** What to do with a card after its cancel result. */
export interface CancelOutcome {
  /** New state for the card, if it changes. */
  state?: 'A';
  /** Info line under the card (unset: a cancelled card reads plain "Cancelled"). */
  message?: string;
  /** Error line on the card; the card is otherwise unchanged. */
  error?: string;
  /** Hide Cancel for good (the server says this cannot be cancelled). */
  notCancellable?: boolean;
  /** The session the cancel created or touched, when the card had none. */
  downloadSessionId?: number;
  /** Remove the card (a placeholder or queued row that no longer exists). */
  remove?: boolean;
  /** Reload the list from GET /download-sessions/recent. */
  reload?: boolean;
  /** One notification to show. */
  notify?: string;
}

/**
 * The ids to send for a card: the session id for a real session, the command
 * id for a queued row or a placeholder that has one. Null when the card has
 * nothing the server can find yet (a fresh placeholder), or is not running.
 */
export function cancelIdsForCard(
  card: DownloadSessionCard
): { commandIds: number[]; downloadSessionIds: number[] } | null {
  if (card.downloadSessionId > 0)
    return { commandIds: [], downloadSessionIds: [card.downloadSessionId] };
  if (card.commandId != null && card.commandId > 0)
    return { commandIds: [card.commandId], downloadSessionIds: [] };
  return null;
}

/**
 * Whether the card shows a Cancel button (before the permission check). It
 * follows the server's `cancellable` flag (EN-1515's rule); `isIridium` (an
 * ID/IDWTR download) does not matter, those are cancellable too.
 */
export function showsCancel(card: DownloadSessionCard): boolean {
  if (card.unknown) return false;
  if (card.state === 'Q') return card.cancellable !== false;
  if (isPlaceholderKey(card.downloadSessionId)) return true;
  if (card.downloadSessionId > 0)
    return (
      (card.state === 'T' || card.state === 'D' || card.state === 'R') &&
      card.cancellable !== false
    );
  return false;
}

/**
 * Whether the panel shows the card's Cancel button: an admin (the same
 * canManageDownloads gate as Retry and queueing) and a card that can be
 * cancelled.
 */
export function showsCancelButton(
  card: DownloadSessionCard,
  access: { canManageDownloads: boolean }
): boolean {
  return access.canManageDownloads && showsCancel(card);
}

/** The result for this card in a cancel response, if any. */
export function findResultForCard(
  card: DownloadSessionCard,
  results: readonly CancelResultInput[] | null | undefined
): CancelResultInput | undefined {
  const list = results ?? [];
  if (card.downloadSessionId > 0) {
    const bySession = list.find(
      (r) => r.downloadSessionId === card.downloadSessionId
    );
    if (bySession) return bySession;
  }
  if (card.commandId != null)
    return list.find((r) => r.commandId === card.commandId);
  return list.length === 1 ? list[0] : undefined;
}

/** Maps one EN-1515 status to what the card shows. */
export function cancelOutcome(
  card: DownloadSessionCard,
  result: CancelResultInput | undefined
): CancelOutcome {
  const waiting =
    isQueuedKey(card.downloadSessionId) ||
    isPlaceholderKey(card.downloadSessionId);
  const sessionId =
    waiting && result?.downloadSessionId != null && result.downloadSessionId > 0
      ? result.downloadSessionId
      : undefined;

  switch (result?.status) {
    // Both end as a plain "Cancelled" card (Dave's request): no extra line.
    case 'Cancelled':
    case 'AlreadyCancelled':
      return { state: 'A', downloadSessionId: sessionId };
    case 'NotCancellable':
      return { error: NOT_CANCELLABLE_MESSAGE, reload: true };
    case 'NotDownload':
      return { error: NOT_DOWNLOAD_MESSAGE, notCancellable: true };
    case 'NotFound':
      return waiting
        ? {
            remove: true,
            reload: true,
            notify: 'That download is no longer queued (or not in your group).',
          }
        : {
            error: 'No longer queued (or not in your group).',
            reload: true,
          };
    case 'Failed':
    default:
      // 'Failed', an unknown status or no result for this card.
      return { error: CANCEL_FAILED_MESSAGE };
  }
}
